import { Dispatch, useCallback, useEffect, useMemo, useRef } from 'react'
import debounce from 'lodash.debounce'
import req from '@sportninja/common/api/request'
import {
  RecentSearches,
  SearchResults,
  SearchAction,
  SearchPagination,
} from './SearchContextProvider'
import { storeRecentSearchTerm } from './recent-search'

export interface SearchResponseItem {
  id?: string | number
  type?: string
  name?: string
  sport?: string
  schedule_type_name?: string
  root?: { name?: string }
  teams?: Array<{
    id?: string | number
    roster_id?: string | number
    jersey_number?: string | number
    position?: string
    name?: string
  }>
  organization?: { name?: string }
  organizations?: Array<{
    id?: string | number
    name?: string
    name_full?: string
  }>
  schedules?: Array<{ id?: string | number; name?: string }>
  address?: {
    street_1?: string
    street_2?: string
    city?: string
  }
  [key: string]: any
}

interface SearchMeta {
  total?: number
  current_page?: number
  last_page?: number
  count?: Record<string, number | undefined>
}

interface SearchApiResponse {
  data: SearchResponseItem[]
  meta: SearchMeta
}

export const createEmptyResults = (): SearchResults => ({
  players: [],
  teams: [],
  scheds: [],
  orgs: [],
  venues: [],
})

interface UseSearchResultsArgs {
  searchText: string
  page: number
  filter: string
  dispatch: Dispatch<SearchAction>
  updateRecentSearches: (value: RecentSearches) => void
}

export const useSearchResults = ({
  searchText,
  page,
  filter,
  dispatch,
  updateRecentSearches,
}: UseSearchResultsArgs) => {
  const abortControllers = useRef<AbortController[]>([])

  const handleSearchSuccess = useCallback(
    ({ data, meta }: SearchApiResponse, text: string, isAppend: boolean) => {
      console.log('🔄 Search: Got results', { count: data.length, meta })
      const players: SearchResponseItem[] = [],
        teams: SearchResponseItem[] = [],
        scheds: SearchResponseItem[] = [],
        orgs: SearchResponseItem[] = [],
        venues: SearchResponseItem[] = []

      data.forEach((d: SearchResponseItem) => {
        if (d.type === 'player') {
          players.push(d)
        } else if (d.type === 'team') {
          teams.push(d)
        } else if (d.type === 'schedule') {
          scheds.push(d)
        } else if (d.type === 'organization') {
          orgs.push(d)
        } else if (d.type === 'venue') {
          venues.push(d)
        }
      })

      if (data.length > 0) {
        // Only set top result on first page
        if (!isAppend) {
          const searchPlayers = data.filter((item) => item.type === 'player')
          if (searchPlayers.length > 0 && data[0]?.type === 'player') {
            const playerWithMostTeams = searchPlayers.reduce(
              (prev, current) => {
                const prevTeams = prev.teams?.length || 0
                const currentTeams = current.teams?.length || 0
                return currentTeams > prevTeams ? current : prev
              }
            )
            dispatch({
              type: 'SET_TOP_RESULT',
              payload: playerWithMostTeams,
            })
          } else {
            dispatch({ type: 'SET_TOP_RESULT', payload: data[0] })
          }
        }
        const store = storeRecentSearchTerm(text)
        if (store) {
          updateRecentSearches(store as RecentSearches)
        }
      }
      dispatch({ type: 'SET_PAGINATION', payload: meta as SearchPagination })

      if (isAppend) {
        dispatch({
          type: 'APPEND_RESULTS',
          payload: { players, teams, scheds, orgs, venues } as SearchResults,
        })
        dispatch({ type: 'SET_LOADING_MORE', payload: false })
      } else {
        dispatch({ type: 'SET_NUM_RESULTS', payload: meta.total ?? 0 })
        dispatch({
          type: 'SET_RESULTS',
          payload: { players, teams, scheds, orgs, venues } as SearchResults,
        })
        dispatch({ type: 'SET_TOTAL_RESULTS', payload: meta.count || {} })
        dispatch({ type: 'SET_LOADING_RESULTS', payload: false })
      }
    },
    [dispatch, updateRecentSearches]
  )

  const handleSearchError = useCallback(
    (errorMessage: string) => {
      console.error('🚨 Search: Error', errorMessage)
      dispatch({ type: 'SET_ERROR', payload: errorMessage })
      dispatch({ type: 'SET_LOADING_RESULTS', payload: false })
      dispatch({ type: 'SET_LOADING_MORE', payload: false })
    },
    [dispatch]
  )

  const debouncedSearchRequest = useMemo(
    () =>
      debounce(
        async (
          text: string,
          nextPage: number,
          nextFilter: string,
          abortSignal: AbortSignal,
          isAppend: boolean
        ) => {
          const query: {
            auto: number
            query: string
            page: number
            filter?: string
          } = { auto: nextFilter ? 0 : 1, query: text, page: nextPage }
          if (nextFilter.length > 0) {
            query.filter = nextFilter
          }
          try {
            const response = (await req('/search/omni', {
              query,
              signal: abortSignal,
            })) as SearchApiResponse
            handleSearchSuccess(response, text, isAppend)
          } catch (err) {
            if (abortSignal.aborted) {
              return
            }
            const message =
              err instanceof Error ? err.message : 'Something went wrong'
            handleSearchError(message)
          }
        },
        400
      ),
    [handleSearchError, handleSearchSuccess]
  )

  useEffect(() => {
    return () => {
      debouncedSearchRequest.cancel()
      abortControllers.current.forEach((controller) => controller.abort())
    }
  }, [debouncedSearchRequest])

  useEffect(() => {
    abortControllers.current.forEach((controller) => controller.abort())
    abortControllers.current = []

    if (searchText.length > 0) {
      console.log('🔄 Search: Searching for', searchText)
      const isAppend = page > 1

      if (isAppend) {
        dispatch({ type: 'SET_LOADING_MORE', payload: true })
      } else {
        dispatch({ type: 'SET_LOADING_RESULTS', payload: true })
        dispatch({ type: 'SET_NUM_RESULTS', payload: false })
        dispatch({ type: 'SET_TOP_RESULT', payload: {} })
        dispatch({ type: 'SET_TOTAL_RESULTS', payload: {} })
        dispatch({ type: 'SET_ERROR', payload: '' })
      }

      const abortController = new AbortController()
      abortControllers.current.push(abortController)

      debouncedSearchRequest(
        searchText,
        page,
        filter,
        abortController.signal,
        isAppend
      )
    } else {
      dispatch({ type: 'SET_LOADING_RESULTS', payload: false })
      dispatch({ type: 'SET_LOADING_MORE', payload: false })
      dispatch({ type: 'SET_NUM_RESULTS', payload: false })
      dispatch({ type: 'SET_TOP_RESULT', payload: {} })
      dispatch({ type: 'SET_TOTAL_RESULTS', payload: {} })
      dispatch({ type: 'SET_PAGINATION', payload: {} })
      dispatch({ type: 'SET_RESULTS', payload: createEmptyResults() })
    }
  }, [debouncedSearchRequest, dispatch, filter, page, searchText])
}
